import { useState } from "react";
import { Layout } from "@/components/Layout";
import { SEO } from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";

const SaltCalculator = () => {
  const [lotSize, setLotSize] = useState<number>(10);
  const [appRate, setAppRate] = useState<number>(800);

  const totalSalt = lotSize * appRate;

  return (
    <Layout>
      <SEO
        title="Salt Calculator | 911 Sourcing"
        description="Calculate how much de-icing salt you need for your properties."
      />

      {/* Hero Section */}
      <section className="relative bg-salt-dark pt-32 pb-20 overflow-hidden">
        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl mx-auto text-center"
          >
            <h1 className="text-4xl md:text-5xl font-black text-white uppercase tracking-tight mb-6">
              Salt <span className="text-salt-orange">Calculator</span>
            </h1>
            <p className="text-xl text-gray-300">
              Quickly determine how much de-icing salt you need per application.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Calculator Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-0 items-center">
            {/* Inputs */}
            <div className="p-4 md:p-8 space-y-12">
              <div>
                <h2 className="text-3xl font-black mb-10">Salt Calculator</h2>

                <div className="space-y-10">
                  {/* Parking Lot Size */}
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <Label className="text-base font-bold text-gray-900">
                        Parking Lot Size
                      </Label>
                      <div className="w-24">
                        <Input
                          type="number"
                          value={lotSize}
                          onChange={(e) => setLotSize(Number(e.target.value))}
                          className="text-center font-bold"
                        />
                      </div>
                    </div>
                    <Slider
                      value={[lotSize]}
                      onValueChange={(vals) => setLotSize(vals[0])}
                      max={100}
                      step={0.01}
                      className="py-4 [&_[data-radix-slider-range]]:bg-salt-orange [&_[data-radix-slider-thumb]]:border-gray-200 [&_[data-radix-slider-thumb]]:shadow-md [&_[data-radix-slider-thumb]]:w-6 [&_[data-radix-slider-thumb]]:h-6"
                    />
                    <div className="flex justify-between text-xs text-gray-500 font-medium">
                      <div className="flex flex-col">
                        <span>0.01</span>
                        <span className="mt-1">Acres</span>
                      </div>
                      <span>100</span>
                    </div>
                  </div>

                  {/* Salt Application Rate */}
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <Label className="text-base font-bold text-gray-900">
                        Salt Application Rate
                      </Label>
                      <div className="w-24">
                        <Input
                          type="number"
                          value={appRate}
                          onChange={(e) => setAppRate(Number(e.target.value))}
                          className="text-center font-bold"
                        />
                      </div>
                    </div>
                    <Slider
                      value={[appRate]}
                      onValueChange={(vals) => setAppRate(vals[0])}
                      max={5000}
                      min={50}
                      step={10}
                      className="py-4 [&_[data-radix-slider-range]]:bg-salt-orange [&_[data-radix-slider-thumb]]:border-gray-200 [&_[data-radix-slider-thumb]]:shadow-md [&_[data-radix-slider-thumb]]:w-6 [&_[data-radix-slider-thumb]]:h-6"
                    />
                    <div className="flex justify-between text-xs text-gray-500 font-medium">
                      <div className="flex flex-col">
                        <span>50</span>
                        <span className="mt-1">Lbs/Acre</span>
                      </div>
                      <span>5,000</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Results */}
            <div className="bg-black text-white p-8 md:p-12 flex flex-col justify-center rounded-2xl shadow-xl lg:ml-8">
              <div className="mb-12">
                <h3 className="text-xl font-bold mb-2">Salt Per Application</h3>
                <div className="text-5xl md:text-6xl font-black mb-2 text-salt-orange">
                  {totalSalt.toLocaleString(undefined, {
                    maximumFractionDigits: 0,
                  })}
                </div>
                <p className="text-gray-400 text-sm">Pounds per Application</p>
              </div>

              <div>
                <h3 className="text-2xl font-bold mb-4">
                  Find your salt today!
                </h3>
                <p className="text-gray-300 mb-8 leading-relaxed text-sm">
                  Now that you know how much salt you need, you need to find the
                  salt. 911 Sourcing can do that for you!
                </p>
                <Button
                  asChild
                  className="w-full bg-salt-orange hover:bg-salt-orange/90 text-white py-6 text-lg font-bold"
                >
                  <Link to="/contact">Contact Us</Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default SaltCalculator;
